import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import deepEqual from 'fast-deep-equal';
import ApiStep from '../../../models/Steps/ApiStep';
import Editor from "@monaco-editor/react";
import { TextField, Select, MenuItem, Button, Chip, Box, Typography, FormControl, InputLabel } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';

interface ApiStepDefinition {
  type: string;
  endpoint: string;
  headers: any;
  params: any;
  body: any;
  auth: any;
  timeout: number;
  condition?: string;
}

interface ApiStepBoxProps {
  stepData: any;
  onUpdate?: (step: any) => void;
  onUpdateImmediate?: (step: any) => void;
  workflowInputs?: {};
  setWorkflowInputs: any
}

const ApiStepBox: React.FC<ApiStepBoxProps> = ({ 
  stepData, 
  onUpdate,
  onUpdateImmediate,
  workflowInputs = {},
  setWorkflowInputs
}) => {
  const [id] = useState<string>(stepData?.id || uuidv4());
  const [description, setDescription] = useState<string>(stepData?.description || 'API Request');
  const [type, setType] = useState<string>(stepData?.definition?.type || 'GET');
  const [endpoint, setEndpoint] = useState<string>(stepData?.definition?.endpoint || '');
  const [headers, setHeaders] = useState<string>(JSON.stringify(stepData?.definition?.headers || {}, null, 2));
  const [params, setParams] = useState<string>(JSON.stringify(stepData?.definition?.params || {}, null, 2));
  const [body, setBody] = useState<string>(JSON.stringify(stepData?.definition?.body || {}, null, 2));
  const [auth, setAuth] = useState<any>(stepData?.definition?.auth || null);
  const [timeout, setTimeoutValue] = useState<string>(JSON.stringify(stepData?.definition?.timeout || 600000, null, 2));
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({ headers: '', params: '', body: '', timeout: '' });
  const [showCondition, setShowCondition] = useState<boolean>(!!stepData?.definition?.condition);
  const [condition, setCondition] = useState<string>(stepData?.definition?.condition || '');

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const validateJSON = (jsonString: string): string | null => {
    try {
      JSON.parse(jsonString);
      return null;
    } catch (error) {
      return "Invalid JSON format";
    }
  };

  const handleJsonChange = (setter: React.Dispatch<React.SetStateAction<string>>, field: string) => 
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setter(value);
      setErrors((prevErrors) => ({ ...prevErrors, [field]: validateJSON(value) || '' }));
    };

  useEffect(() => {
    if (!errors.headers && !errors.params && !errors.body && !errors.timeout) {
      const definition: ApiStepDefinition = {
        type,
        endpoint,
        headers: JSON.parse(headers),
        params: JSON.parse(params),
        body: JSON.parse(body),
        auth,
        timeout: JSON.parse(timeout),
        ...(showCondition && condition ? { condition } : {})
      };

      const updatedStep = new ApiStep(description, definition);
      updatedStep.id = id;

      if (!deepEqual(updatedStep, stepData) && onUpdate) {
        onUpdate(updatedStep);
      }
    }
  }, [id, description, type, endpoint, headers, params, body, auth, condition, timeout, stepData, onUpdate, errors, showCondition]);

  return (
    <div className="p-4 bg-gray-100 rounded-md border border-gray-300 text-sm">
      {Object.keys(workflowInputs).length > 0 && (
        <Box className="mb-4 p-2 bg-red-50 border border-red-200 rounded">
          <Typography variant="subtitle2" className="text-red-600 font-medium mb-1">Available Variables:</Typography>
          <Box className="flex flex-wrap gap-2">
            {Object.entries(workflowInputs).map(([key, value]) => (
              <Chip key={key} label={`${key}: ${value}`} color="error" size="small" />
            ))}
          </Box>
        </Box>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={toggleCollapse}
            className="mr-2 focus:outline-none"
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? '▶' : '▼'}
          </button>
          <h3 className="font-bold">API Step: Call third Party API</h3>
        </div>
      </div>

      {!isCollapsed && (
        <div className="mt-2 text-xs text-gray-700">
          <Box className="mb-2">
            <Typography variant="subtitle2" className="block font-bold">ID:</Typography>
            <TextField
              value={id}
              InputProps={{ readOnly: true }}
              className="bg-gray-200 cursor-not-allowed"
              fullWidth
              size="small"
            />
          </Box>

          <Box className="mb-2">
            <Typography variant="subtitle2" className="block font-bold">Description:</Typography>
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>

          <Box className="mb-2">
            <Typography variant="subtitle2" className="block font-bold">Request Type:</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box className="mb-2">
            <Typography variant="subtitle2" className="block font-bold">Endpoint:</Typography>
            <TextField
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              fullWidth
              size="small"
            />
          </Box>

          <Box className="mb-2">
            <Typography variant="subtitle2" className="block text-xs font-bold mb-1">Headers (JSON)</Typography>
            <TextField
              value={headers}
              onChange={handleJsonChange(setHeaders, 'headers')}
              rows={4}
              placeholder="Enter JSON"
              multiline
              fullWidth
              size="small"
            />
            {errors.headers && <Typography variant="caption" color="error" className="mt-1">{errors.headers}</Typography>}
          </Box>

          <Box className="mb-2">
            <Typography variant="subtitle2" className="block text-xs font-bold mb-1">Params (JSON)</Typography>
            <TextField
              value={params}
              onChange={handleJsonChange(setParams, 'params')}
              rows={4}
              placeholder="Enter JSON"
              multiline
              fullWidth
              size="small"
            />
            {errors.params && <Typography variant="caption" color="error" className="mt-1">{errors.params}</Typography>}
          </Box>

          {type === 'POST' && (
            <Box className="mb-2">
              <Typography variant="subtitle2" className="block text-xs font-bold mb-1">Body (JSON)</Typography>
              <TextField
                value={body}
                onChange={handleJsonChange(setBody, 'body')}
                rows={4}
                placeholder="Enter JSON"
                multiline
                fullWidth
                size="small"
              />
              {errors.body && <Typography variant="caption" color="error" className="mt-1">{errors.body}</Typography>}
            </Box>
          )}

          <Box className="mb-2">
            <Typography variant="subtitle2" className="block text-xs font-bold mb-1">Timeout (milliseconds)</Typography>
            <TextField
              value={timeout}
              onChange={handleJsonChange(setTimeoutValue, 'timeout')}
              rows={1}
              placeholder="Enter timeout in milliseconds (default: 600000)"
              multiline
              fullWidth
              size="small"
            />
            {errors.timeout && <Typography variant="caption" color="error" className="mt-1">{errors.timeout}</Typography>}
          </Box>

          <Box className="mb-2">
            {!showCondition ? (
              <Button 
                variant="outlined"
                startIcon={<AddIcon />} 
                onClick={() => setShowCondition(true)}
                className="w-full"
                fullWidth
              >
                Add Condition
              </Button>
            ) : (
              <Box>
                <Box className="flex items-center justify-between mb-1">
                  <Typography variant="subtitle2" className="block font-bold">Step Condition (Python Code):</Typography>
                  <Button 
                    variant="text"
                    color="error"
                    startIcon={<RemoveIcon />} 
                    onClick={() => {
                      setShowCondition(false);
                      setCondition('');
                    }}
                  >
                    Remove
                  </Button>
                </Box>
                <Box className="border border-gray-300 rounded-md">
                  <Editor
                    height="30px"
                    defaultLanguage="python"
                    value={condition}
                    onChange={(value) => setCondition(value || '')}
                    theme="vs-light"
                    options={{
                      minimap: { enabled: false },
                      lineNumbers: 'off',
                      glyphMargin: false,
                      folding: false,
                      lineDecorationsWidth: 0,
                      lineNumbersMinChars: 0,
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </div>
      )}
    </div>
  );
};

export default React.memo(ApiStepBox);